import React from "react";
import { observer, inject } from "mobx-react";

import { SvgIconProps } from "@material-ui/core/SvgIcon";
import { IAuthStore, ISessionTokenLocation } from "@src/Stores/Auth/types";
import { IValuesStore, IRoles } from "@src/Stores/Values/types";
import { ListSubheader } from "@material-ui/core";

import PinDrop from "@material-ui/icons/PinDrop";
import Business from "@material-ui/icons/Business";
import GroupWork from "@material-ui/icons/GroupWork";
import Work from "@material-ui/icons/Work";
import WorkOutline from "@material-ui/icons/WorkOutline";
import People from "@material-ui/icons/People";
import Settings from "@material-ui/icons/Settings";
import Brush from "@material-ui/icons/Brush";
import Lock from "@material-ui/icons/Lock";
import Person from "@material-ui/icons/Person";
import Dialpad from "@material-ui/icons/Dialpad";
import PartyMode from "@material-ui/icons/PartyMode";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import ScreenShare from "@material-ui/icons/ScreenShare";
import Assessment from "@material-ui/icons/Assessment";
import Notes from "@material-ui/icons/Notes";
import PeopleOutline from "@material-ui/icons/PeopleOutline";
import Home from '@material-ui/icons/Home';
import LockOpen from '@material-ui/icons/LockOpenOutlined';

import MenuItem from "./MenuItem/MenuItem";
import { Values } from "@src/Tools/Values";
import { RouterStore } from "mobx-react-router";
import { STContainerMenuList } from "@src/Components/Ui/STMenuListContainer";
import VersionMenuItem from "@src/Components/Ui/VersionMenuItem/VersionMenuItem";
import _ from "lodash";
import { Fingerprint } from "@material-ui/icons";

export interface IMenuListProps {
  auth?: IAuthStore;
  values?: IValuesStore;
  routing?: RouterStore;
  onDrawerToggle: () => void;
}

export interface IClause {
  to: string;
  text: string;
  icon: React.ComponentType<SvgIconProps>;
}

export class ClauseStore {
  private clauses: IClause[];
  private userRoles: string[];
  private userLocation: ISessionTokenLocation;
  private masterPossibility: boolean;
  private rolesList: IRoles;

  constructor(userRoles: string[], rolesList: IRoles, userLocation: ISessionTokenLocation) {
    this.clauses = [];
    this.userRoles = userRoles;
    this.userLocation = userLocation;
    this.rolesList = rolesList;
    this.masterPossibility =
      userRoles.includes(rolesList.Administrator);
  }

  public withClause = (
    to: string,
    localizationKey: string,
    icon: React.ComponentType<SvgIconProps>,
    roles?: string[],
    onlyAdminPossibilities?: boolean,
    onlyPrimaryLocation?: boolean
  ): ClauseStore => {
    let hasPermission = onlyAdminPossibilities
        ? this.userRoles.includes(this.rolesList.Administrator)
        : _.isNil(roles) || this.masterPossibility || this.userRoles.filter(value => roles.includes(value)).length > 0;

    if (onlyPrimaryLocation && hasPermission) {
      hasPermission = this.userLocation.primary;
    }

    if (hasPermission) {
      this.clauses.push({
        to: to,
        text: Values.getLocalizationValue(localizationKey),
        icon: icon,
      });
    }
    return this;
  };

  public getClauses = (): IClause[] => {
    return this.clauses;
  };
}

@inject("auth", "values", "routing")
@observer
export default class MenuList extends React.Component<IMenuListProps> {
  createClauseStore = () => {
    const userRoles = this.props.auth.user.roles;
    const rolesList: IRoles = this.props.values.roles;
    const userLocation = this.props.auth.user.location;
    return new ClauseStore(userRoles, rolesList, userLocation);
  };

  getCommonItems = (): IClause[] => {
    return this.createClauseStore()
        .withClause("/", "home", Home)
        .getClauses();
  }

  getApplicationItems = (): IClause[] => {
    const roles: IRoles = this.props.values.roles;
    return this.createClauseStore()
        .withClause("/application-catalogue", "applicationCatalogue", PartyMode, [roles.Administrator], null, false)
        .getClauses()
  }

  getOrganizationSettingsItems = (): IClause[] => {
    const roles: IRoles = this.props.values.roles;
      return this.createClauseStore()
          .withClause("/organisations", "organisations", Business, [roles.Manager, roles.Administrator] , null, true)
          .withClause("/persons", "persons", People, [roles.Manager, roles.Administrator])
          .withClause("/position-types", "positionTypes", Work, [roles.Manager, roles.Administrator], null, true)
          .withClause("/position-teams", "positionTeams", WorkOutline, [roles.Manager, roles.Administrator])
          .withClause("/locations", "locations", PinDrop, [roles.Manager, roles.Administrator])
      .getClauses();
  };

  getLocationSettingsItems = (): IClause[] => {
    const roles: IRoles = this.props.values.roles;
    return this.createClauseStore()
      .withClause("/position-management", "positionManagement", Work, [roles.Manager, roles.Administrator], null, true)
      .withClause("/crew", "crew", PeopleOutline, [roles.Manager, roles.Administrator])
      .getClauses();
  };

  getPermissionConfigurationItems = (): IClause[] => {
    const roles: IRoles = this.props.values.roles;
    return this.createClauseStore()
      .withClause("/permissions", "permissions", Lock,[roles.Manager, roles.Administrator], null, true)
      .withClause("/permission-groups", "permissionGroups", GroupWork, [roles.Manager, roles.Administrator], null, true)
      .getClauses();
  };

  getSystemSettingsItems = (): IClause[] => {
    const roles: IRoles = this.props.values.roles;
    return this.createClauseStore()
      .withClause("/themes", "themes", Brush, [roles.Manager, roles.Administrator])
      .withClause("/applications", "applications", PartyMode, [roles.Manager, roles.Administrator])
      .withClause("/app-permissions", "applicationPermissions", Lock, [roles.Manager, roles.Administrator])
      .withClause("/api", "api", ScreenShare, [roles.Manager, roles.Administrator])
      .withClause("/audit-log", "auditLog", Notes, [roles.Manager, roles.Administrator])
      .withClause("/web-socket-sessions", "webSocketSessions", Assessment, [roles.Manager, roles.Administrator])
      .getClauses();
  };

  getSystemConfigurationItems = (): IClause[] => {
    const roles: IRoles = this.props.values.roles;
    return this.createClauseStore()
      .withClause("/oauth", "authorizationAndAuthentication", Fingerprint, null, true)
      .withClause("/options", "options", Settings, [roles.Manager, roles.Administrator], null, true)
      .withClause("/system-settings", "systemSettings", Settings, null, true)
      .getClauses();
  };

  getPersonalSettingsItems = (): IClause[] => {
      if (this.props.auth.user.roles.find(x => x == this.props.values.roles.Administrator) != null) {
          return this.createClauseStore()
              .withClause("/profile", "profile", Person)
              .withClause("/2fa", "Two-factor auth", LockOpen)
              .getClauses();
    }
    return this.createClauseStore()
      .withClause("/profile", "profile", Person)
      .withClause("/profile/password", "changePassword", Lock)
      .withClause("/profile/pin", "quickLogin", Dialpad)
      .withClause("/profile/image", "profileImage", PhotoCamera)
      .withClause("/2fa", "Two-factor auth", LockOpen)
      .getClauses();
  };

  render() {
    const commonItems = this.getCommonItems();
    const organizationSettingsItems = this.getOrganizationSettingsItems();
    const locationSettingsItems = this.getLocationSettingsItems();
    const applicationCatalogueItems = this.getApplicationItems();
    const permissionConfigurationItems = this.getPermissionConfigurationItems();
    const systemSettingsItems = this.getSystemSettingsItems();
    const systemConfigurationItems = this.getSystemConfigurationItems();
    const personalSettingsItems = this.getPersonalSettingsItems();

    if (
      organizationSettingsItems.length === 0 &&
      locationSettingsItems.length === 0 &&
      systemSettingsItems.length === 0
    ) {
      if (this.props.routing.history.location.pathname !== "/2fa") {
        this.props.routing.history.push("/");
      }
    }

    return (
      <React.Fragment>
        <STContainerMenuList>
          {this.renderItems("", commonItems)}
          {this.renderItems("organizationSettings", organizationSettingsItems)}
          {this.renderItems("applications",applicationCatalogueItems)}
          {this.renderItems("locationSettings", locationSettingsItems)}
          {this.renderItems("permissionConfiguration", permissionConfigurationItems)}
          {this.renderItems("systemSettings", systemSettingsItems)}
          {this.renderItems("systemConfiguration", systemConfigurationItems)}
          {this.renderItems("personalSettings", personalSettingsItems)}
          <VersionMenuItem />
        </STContainerMenuList>
      </React.Fragment>
    );
  }

  renderItems = (titleLocalizationKey: string, items: IClause[]) => {
    if (items.length > 0) {
      return (
        <>
          { titleLocalizationKey != "" && (
              <ListSubheader component="div" disableSticky={true}>
                {Values.getLocalizationValue(titleLocalizationKey)}
              </ListSubheader>
          )}
          {this.renderMenuItems(items)}
        </>
      );
    }
    return null;
  };

  renderMenuItems = (clausesProfile: IClause[]) => {
    return clausesProfile.map((item: IClause, index: number) => (
      <MenuItem key={index} to={item.to} text={item.text} icon={item.icon} onDrawerToggle={this.props.onDrawerToggle} />
    ));
  };
}
